import { useEffect, useState } from "react"

import {
  EUSignLibrary,
  EUSignLibraryLoader,
  type EUSignLibraryAdapter,
} from "iit/browser"

type EuSignState =
  | { status: "loading"; adapter: null }
  | { status: "ready"; adapter: EUSignLibraryAdapter }
  | { status: "error"; adapter: null }

let loadPromise: Promise<void> | null = null

// `EUSignLibraryLoader.load()` never rejects — both its success and failure
// paths call `resolve(...)` (a loaded adapter, or one carrying `initializeError`).
// So "did it fail" has to be read from `EUSignLibrary.getInstance()` after the
// promise settles, not from a `.catch()`.
function ensureLoaded(): Promise<void> {
  if (!loadPromise) {
    loadPromise = new EUSignLibraryLoader().load().then(() => undefined)
  }
  return loadPromise
}

/**
 * Lazily loads the EUSign (КЕП) library the first time `KepKeyReader`
 * mounts, instead of blocking the app bootstrap. The underlying load is
 * memoized module-wide so later signing flows do not reload the library.
 */
export function useEuSign(): EuSignState {
  const [state, setState] = useState<EuSignState>(() => {
    const instance = EUSignLibrary.getInstance()
    return instance
      ? { status: "ready", adapter: instance }
      : { status: "loading", adapter: null }
  })

  useEffect(() => {
    if (state.status !== "loading") return
    let cancelled = false
    ensureLoaded().then(() => {
      if (cancelled) return
      const instance = EUSignLibrary.getInstance()
      setState(
        instance
          ? { status: "ready", adapter: instance }
          : { status: "error", adapter: null }
      )
    })
    return () => {
      cancelled = true
    }
  }, [state.status])

  return state
}
